import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { X, MapPin, Calendar, DollarSign, Clock, User, FileText, Users, FileDown } from 'lucide-react';
import { exportTicketAsInvoice, InvoiceSettings } from '../utils/invoiceGenerator';

interface TicketDetailProps {
  ticketId: string;
  onClose: () => void;
  onUpdate?: () => void;
}

interface TicketData {
  id: string;
  ticket_number: string;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  location_details: string | null;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  property: {
    name: string;
    address: string;
    owner_name?: string | null;
    owner_email?: string | null;
    owner_phone?: string | null;
    billing_address?: string | null;
  };
  assigned_personnel?: {
    name: string;
  } | null;
}

interface TicketUpdate {
  id: string;
  update_type: string;
  notes: string | null;
  cost: number | null;
  hours_worked: number | null;
  materials_used: string | null;
  old_status: string | null;
  new_status: string | null;
  created_at: string;
  created_by: string;
}

export function TicketDetail({ ticketId, onClose, onUpdate }: TicketDetailProps) {
  const [ticket, setTicket] = useState<TicketData | null>(null);
  const [updates, setUpdates] = useState<TicketUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpdateForm, setShowUpdateForm] = useState(false);

  useEffect(() => {
    loadTicketData();
  }, [ticketId]);

  const loadTicketData = async () => {
    setLoading(true);
    try {
      const [ticketResult, updatesResult] = await Promise.all([
        supabase
          .from('tickets')
          .select('*, property:properties(name, address, owner_name, owner_email, owner_phone, billing_address), assigned_personnel:maintenance_personnel(name)')
          .eq('id', ticketId)
          .single(),
        supabase
          .from('ticket_updates')
          .select('*')
          .eq('ticket_id', ticketId)
          .order('created_at', { ascending: false }),
      ]);

      if (ticketResult.error) throw ticketResult.error;
      if (updatesResult.error) throw updatesResult.error;

      const formattedTicket = {
        ...ticketResult.data,
        property: Array.isArray(ticketResult.data.property)
          ? ticketResult.data.property[0]
          : ticketResult.data.property,
        assigned_personnel: Array.isArray(ticketResult.data.assigned_personnel)
          ? ticketResult.data.assigned_personnel[0]
          : ticketResult.data.assigned_personnel,
      } as TicketData;

      setTicket(formattedTicket);
      setUpdates(updatesResult.data || []);
    } catch (error) {
      console.error('Error loading ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      open: 'bg-secondary-100 text-secondary-800 border-secondary-200',
      assigned: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      closed: 'bg-gray-100 text-gray-800 border-gray-200',
    };
    return colors[status as keyof typeof colors] || colors.open;
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-secondary-100 text-secondary-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800',
    };
    return colors[priority as keyof typeof colors] || colors.medium;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalCost = updates.reduce((sum, update) => sum + (update.cost || 0), 0);
  const totalHours = updates.reduce((sum, update) => sum + (update.hours_worked || 0), 0);

  const handleUpdateSuccess = () => {
    loadTicketData();
    setShowUpdateForm(false);
    if (onUpdate) onUpdate();
  };

  const handleExportInvoice = async () => {
    if (!ticket) return;

    try {
      const { data: settings } = await supabase
        .from('invoice_settings')
        .select('*')
        .maybeSingle();

      exportTicketAsInvoice({
        ticketNumber: ticket.ticket_number,
        propertyName: ticket.property.name,
        propertyAddress: ticket.property.address,
        ownerName: ticket.property.owner_name,
        ownerEmail: ticket.property.owner_email,
        ownerPhone: ticket.property.owner_phone,
        billingAddress: ticket.property.billing_address,
        title: ticket.title,
        description: ticket.description,
        category: ticket.category,
        priority: ticket.priority,
        status: ticket.status,
        locationDetails: ticket.location_details,
        assignedPersonnel: ticket.assigned_personnel?.name || null,
        createdAt: ticket.created_at,
        completedAt: ticket.completed_at,
        updates: updates.map(update => ({
          date: update.created_at,
          personnelName: update.created_by,
          notes: update.notes,
          cost: update.cost,
          hoursWorked: update.hours_worked,
          materialsUsed: update.materials_used,
        })),
        totalCost,
        totalHours,
      }, settings as InvoiceSettings | undefined);
    } catch (error) {
      console.error('Error exporting invoice:', error);
      alert('Failed to export invoice. Please try again.');
    }
  };

  if (loading || !ticket) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="text-gray-600">Loading ticket details...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{ticket.ticket_number}</h2>
            <div className="flex items-center gap-3 mt-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(ticket.status)}`}>
                {ticket.status.replace('_', ' ').toUpperCase()}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(ticket.priority)}`}>
                {ticket.priority.toUpperCase()}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportInvoice}
              className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              title="Export as Invoice"
            >
              <FileDown size={16} />
              <span className="hidden sm:inline">Invoice</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">{ticket.title}</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <MapPin className="text-gray-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <div className="font-semibold text-gray-800">{ticket.property.name}</div>
                <div className="text-sm text-gray-600">{ticket.property.address}</div>
                {ticket.location_details && (
                  <div className="text-sm text-gray-600 mt-1">Location: {ticket.location_details}</div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FileText className="text-gray-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <div className="text-sm text-gray-600">Category</div>
                <div className="font-semibold text-gray-800 capitalize">{ticket.category.replace('_', ' ')}</div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="text-gray-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <div className="text-sm text-gray-600">Created</div>
                <div className="font-semibold text-gray-800">{formatDate(ticket.created_at)}</div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="text-gray-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <div className="text-sm text-gray-600">Last Updated</div>
                <div className="font-semibold text-gray-800">{formatDate(ticket.updated_at)}</div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Users className="text-gray-400 flex-shrink-0 mt-1" size={20} />
              <div>
                <div className="text-sm text-gray-600">Assigned To</div>
                <div className="font-semibold text-gray-800">
                  {ticket.assigned_personnel?.name || 'Unassigned'}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <DollarSign className="text-green-600" size={24} />
              <div>
                <div className="text-sm text-gray-600">Total Cost</div>
                <div className="text-xl font-bold text-gray-800">${totalCost.toFixed(2)}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="text-secondary-600" size={24} />
              <div>
                <div className="text-sm text-gray-600">Total Hours</div>
                <div className="text-xl font-bold text-gray-800">{totalHours.toFixed(1)}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Update History</h3>
              <button
                onClick={() => setShowUpdateForm(!showUpdateForm)}
                className="px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-secondary-700 transition-colors"
              >
                {showUpdateForm ? 'Cancel' : 'Add Update'}
              </button>
            </div>

            {showUpdateForm && (
              <UpdateForm
                ticketId={ticketId}
                currentStatus={ticket.status}
                onSuccess={handleUpdateSuccess}
                onCancel={() => setShowUpdateForm(false)}
              />
            )}

            {updates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No updates yet</div>
            ) : (
              <div className="space-y-4">
                {updates.map((update) => (
                  <div key={update.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <User className="text-gray-400" size={18} />
                        <span className="font-semibold text-gray-800">{update.created_by}</span>
                        <span className="text-sm text-gray-500">{formatDate(update.created_at)}</span>
                      </div>
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                        {update.update_type.replace('_', ' ')}
                      </span>
                    </div>

                    {update.old_status && update.new_status && (
                      <div className="mb-2 text-sm">
                        Status changed: <span className="font-medium">{update.old_status}</span> →{' '}
                        <span className="font-medium">{update.new_status}</span>
                      </div>
                    )}

                    {update.notes && (
                      <p className="text-gray-700 mb-2 whitespace-pre-wrap">{update.notes}</p>
                    )}

                    <div className="flex flex-wrap gap-4 text-sm">
                      {update.cost !== null && (
                        <span className="text-green-700 font-medium">Cost: ${update.cost.toFixed(2)}</span>
                      )}
                      {update.hours_worked !== null && (
                        <span className="text-secondary-700 font-medium">Hours: {update.hours_worked}</span>
                      )}
                      {update.materials_used && (
                        <span className="text-gray-600">Materials: {update.materials_used}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface UpdateFormProps {
  ticketId: string;
  currentStatus: string;
  onSuccess: () => void;
  onCancel: () => void;
}

function UpdateForm({ ticketId, currentStatus, onSuccess, onCancel }: UpdateFormProps) {
  const [loading, setLoading] = useState(false);
  const [personnel, setPersonnel] = useState<Array<{ id: string; name: string }>>([]);
  const [formData, setFormData] = useState({
    updateType: 'note',
    notes: '',
    cost: '',
    hoursWorked: '',
    materialsUsed: '',
    newStatus: currentStatus,
    createdBy: '',
  });

  useEffect(() => {
    loadPersonnel();
  }, []);

  const loadPersonnel = async () => {
    const { data, error } = await supabase
      .from('maintenance_personnel')
      .select('id, name')
      .eq('active', true)
      .order('name');

    if (error) {
      console.error('Error loading personnel:', error);
    } else {
      setPersonnel(data || []);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData: any = {
        ticket_id: ticketId,
        update_type: formData.updateType,
        notes: formData.notes || null,
        cost: formData.cost ? parseFloat(formData.cost) : null,
        hours_worked: formData.hoursWorked ? parseFloat(formData.hoursWorked) : null,
        materials_used: formData.materialsUsed || null,
        created_by: formData.createdBy,
      };

      if (formData.newStatus !== currentStatus) {
        updateData.old_status = currentStatus;
        updateData.new_status = formData.newStatus;

        await supabase
          .from('tickets')
          .update({
            status: formData.newStatus,
            ...(formData.newStatus === 'completed' ? { completed_at: new Date().toISOString() } : {}),
          })
          .eq('id', ticketId);
      }

      const { error } = await supabase.from('ticket_updates').insert(updateData);

      if (error) throw error;

      onSuccess();
    } catch (error) {
      console.error('Error adding update:', error);
      alert('Failed to add update. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mb-6 p-4 bg-secondary-50 border border-secondary-200 rounded-lg space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Update Type</label>
          <select
            value={formData.updateType}
            onChange={(e) => setFormData({ ...formData, updateType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
          >
            <option value="note">Note</option>
            <option value="status_change">Status Change</option>
            <option value="cost_added">Cost Added</option>
            <option value="time_logged">Time Logged</option>
            <option value="materials">Materials</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Personnel *</label>
          <select
            required
            value={formData.createdBy}
            onChange={(e) => setFormData({ ...formData, createdBy: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
          >
            <option value="">Select personnel</option>
            {personnel.map((person) => (
              <option key={person.id} value={person.name}>
                {person.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={formData.newStatus}
            onChange={(e) => setFormData({ ...formData, newStatus: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
          >
            <option value="open">Open</option>
            <option value="assigned">Assigned</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cost ($)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.cost}
            onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
            placeholder="0.00"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hours Worked</label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={formData.hoursWorked}
            onChange={(e) => setFormData({ ...formData, hoursWorked: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
            placeholder="0.0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Materials Used</label>
          <input
            type="text"
            value={formData.materialsUsed}
            onChange={(e) => setFormData({ ...formData, materialsUsed: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
            placeholder="List materials"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
          placeholder="Add detailed notes about this update"
        />
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-secondary-700 transition-colors disabled:bg-gray-400"
        >
          {loading ? 'Saving...' : 'Save Update'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
